import { createTheme } from '@mui/material/styles'
import klh from './klhPalette'
import { chartCategorical } from './chartPalette'

/**
 * Tema MUI — menjembatani komponen MUI (DataGrid, date pickers, Button toolbar)
 * ke palet Design System KLH/BPLH v2.1.
 *
 * Tipografi sengaja TIDAK diatur di sini: font DS baru di-load pada Fase 2,
 * dan mengganti fontFamily sebelum fontnya ada hanya menghasilkan fallback
 * sistem yang berbeda dari tampilan sekarang.
 *
 * Header tabel memakai hijau --g-700 dengan teks putih uppercase sesuai .tbl
 * design system; kolom bertipe angka memakai font mono dan rata kanan.
 */
const muiTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: klh.green[600], light: klh.green[500], dark: klh.green[700], contrastText: '#FFFFFF' },
    secondary: { main: klh.blue[600], light: klh.blue[500], dark: klh.blue[700], contrastText: '#FFFFFF' },
    success: { main: klh.status.success, light: klh.success[400], dark: klh.success[700], contrastText: '#FFFFFF' },
    warning: { main: klh.status.warning, light: klh.orange[500], dark: klh.orange[800], contrastText: '#FFFFFF' },
    error: { main: klh.status.danger, light: klh.danger[400], dark: klh.danger[700], contrastText: '#FFFFFF' },
    info: { main: klh.status.info, light: klh.blue[500], dark: klh.blue[800], contrastText: '#FFFFFF' },
    background: { default: klh.neutral.bg, paper: klh.neutral.surface },
    text: { primary: klh.ink[900], secondary: klh.ink[500], disabled: klh.ink[300] },
    divider: klh.neutral.line,
    action: {
      hover: klh.neutral.surface2,
      selected: klh.green[50],
      disabled: klh.ink[300],
      disabledBackground: klh.neutral.surface3,
    },
  },

  shape: { borderRadius: 12 }, // --r-md

  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          // v7 membaca latar header dari CSS variable ini, bukan dari
          // .MuiDataGrid-columnHeaders, jadi keduanya diset.
          '--DataGrid-containerBackground': klh.green[700],
          '--DataGrid-rowBorderColor': klh.neutral.line,
          backgroundColor: klh.neutral.surface,
          border: `1px solid ${klh.neutral.line}`,
          borderRadius: klh.radius.md,
          color: klh.ink[900],
          fontSize: '0.88rem',
        },
        columnHeaders: { backgroundColor: klh.green[700] },
        // Warna putih diset di tingkat header supaya ikon urut dan menu kolom
        // ikut terbaca di atas latar hijau, bukan hanya teks judulnya.
        columnHeader: {
          color: '#FFFFFF',
          '& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton, & .MuiDataGrid-filterIcon': {
            color: '#FFFFFF',
            opacity: 0.7,
          },
        },
        columnHeaderTitle: {
          fontWeight: 600,
          fontSize: 12,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: '#FFFFFF',
          whiteSpace: 'nowrap',
        },
        columnSeparator: { color: 'rgba(255,255,255,0.25)' },
        cell: {
          borderColor: klh.neutral.line,
          // Kolom yang dideklarasikan type: 'number' otomatis rata kanan;
          // DS meminta angka memakai font mono (.tbl .num).
          '&.MuiDataGrid-cell--textRight': {
            fontFamily: klh.font.mono.map((f) => (f.includes(' ') ? `'${f}'` : f)).join(', '),
          },
        },
        row: {
          '&:hover': { backgroundColor: klh.neutral.surface2 },
          '&.Mui-selected': {
            backgroundColor: klh.green[50],
            '&:hover': { backgroundColor: klh.green[100] },
          },
        },
        footerContainer: { borderColor: klh.neutral.line },
        overlay: { backgroundColor: klh.neutral.surface },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: klh.radius.sm },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },

    // @mui/x-charts membaca defaultProps lewat useThemeProps, jadi palet seri
    // bisa diset sekali di sini alih-alih per grafik di 11 berkas.
    // Urutan warna bersifat tetap dan tidak diputar (aturan palet kategorikal).
    MuiBarChart: { defaultProps: { colors: chartCategorical } },
    MuiLineChart: { defaultProps: { colors: chartCategorical } },
    MuiPieChart: { defaultProps: { colors: chartCategorical } },
    MuiScatterChart: { defaultProps: { colors: chartCategorical } },
    MuiSparkLineChart: { defaultProps: { colors: chartCategorical } },
  },
})

export default muiTheme
